import React, {useState} from 'react';
import {Link} from 'react-router-dom';
import Icon from '@/shared/components/Icon';
import clsx from 'clsx';
import {resolveMediaUrl} from '@/shared/utils/mediaUrl';
import {useCart} from '@/modules/shop/context/CartContext';
import {customKitLineKey, curatedRecLineKey} from '@/modules/shop/types';
import biomatchKitBg from '@/assets/dashboard/biomatch_kit_bg.jpg';
import biomatchProductBg from '@/assets/dashboard/biomatch_product_bg.jpg';
import biomatchKitBox from '@/assets/dashboard/biomatch_kit_box.png';
import biomatchIcon from '@/assets/dashboard/biomatch-icon.svg';
import {cleanBiomarkerName} from '@/shared/utils/biomarkerUtils';
import {applyMemberDiscount} from '@mld/shared/pricing/memberDiscount';
import {useBioMatchMarkers} from '../hooks/useBioMatchMarkers';
import {hasBioMatchCardContent, SHOW_CURATED_CARDS} from '../hooks/biomatchCardVisibility';
import type {CuratedCard as CuratedCardData} from '../services/bioMatchEnrollmentService';

import SectionHeader from '@/shared/components/SectionHeader';
import type {CustomLabPackage, CustomLabPackageProduct} from '../services/bioMatchEnrollmentService';

/**
 * BioMatch patient hero — right sidebar of the dashboard.
 *
 * Rebuilt to `mld-dash-26-v11.psd`. The card now sells one thing: the
 * storefront product BioMatch matched to this patient. Its name,
 * description, price and panel contents all come from that product, so
 * a package without one renders nothing.
 *
 * Vertical order on the teal card:
 *   1. "BioMatch" pill + "Your Next Test is Ready." intro.
 *   2. Kit box image (fixed brand art, identical for every product).
 *   3. "Matched based on your labs" pill, product name, description.
 *   4. "Order Your Kit" + one-time price, with the member price
 *      linking through to membership signup.
 *   5. "Included In Your Panel" — the product's assays as one flowing
 *      bullet-separated list.
 *
 * The patient's own out-of-range/borderline markers used to live on this
 * card; v11 split them into `BiomarkersNeedAttentionCard`, which now
 * derives from the patient's full results via `useAbnormalBiomarkers`
 * (patient-dashboard hooks) — this card and hook are kit-only.
 */

interface Props {
    className?: string;
}

const formatUSD = (cents: number): string => {
    const dollars = cents / 100;
    return dollars.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        // Always two decimals — a whole-dollar kit should read "$180.00",
        // not "$180", beside the discounted member price.
        minimumFractionDigits: 2,
    });
};

/**
 * TEMPORARILY DISABLED (2026-07-29, per Derrick): hides the curated
 * "BioMatched For You" cards section. Products are now built INTO the
 * BioMatch itself (product-only recs whose SKU bundles kit + assays +
 * supplement), so standalone product cards would double-sell alongside
 * the custom package. Flip to true to bring the section back — do not
 * delete the render path below.
 */

const CustomBioMatchHero: React.FC<Props> = ({className}) => {
    const {isLoading, isError, customPackage, curatedCards, product, memberDiscountPct, productIncludedAssays} =
        useBioMatchMarkers();

    // Single-item carousel index for the curated cards. When the patient
    // has multiple curated recs, the design cycles through them one-at-a-
    // time inside the BioMatched For You card.
    const [curatedIdx, setCuratedIdx] = useState(0);

    // Render nothing while loading or on error/empty: the section
    // only appears when it has content. Avoids the flash sequence
    // of "big empty card with spinner → section vanishes" that
    // happens when a user has no recommendations (common early in
    // a campaign or for orgs without BioMatch enrolled). The
    // query's 60s staleTime means repeat page-loads are instant
    // anyway; only the first paint shows nothing-then-content.
    // Shared predicate: the My Labs Plus card keys off the same check, so
    // "hero renders" and "membership renders" can never disagree.
    if (!hasBioMatchCardContent({isLoading, isError, customPackage, product, curatedCards})) return null;
    const showCustomCard = Boolean(customPackage && product);

    const safeIdx = Math.max(0, Math.min(curatedIdx, curatedCards.length - 1));
    const currentCurated = curatedCards[safeIdx];

    return (
        <div className={clsx('space-y-6', className)}>
            {showCustomCard && customPackage && product && (
                <CustomCard
                    pkg={customPackage}
                    product={product}
                    includedAssays={productIncludedAssays}
                    memberDiscountPct={memberDiscountPct}
                />
            )}
            {SHOW_CURATED_CARDS && currentCurated && (
                <section className="space-y-3">
                    <SectionHeader title="BioMatched For You" />
                    <CuratedCard
                        key={currentCurated.id}
                        rec={currentCurated}
                        onPrev={() => setCuratedIdx((i) => (i - 1 + curatedCards.length) % curatedCards.length)}
                        onNext={() => setCuratedIdx((i) => (i + 1) % curatedCards.length)}
                        index={safeIdx}
                        total={curatedCards.length}
                        memberDiscountPct={memberDiscountPct}
                    />
                </section>
            )}
        </div>
    );
};

// ── BioMatch kit card ──────────────────────────────────────────────────

const CustomCard: React.FC<{
    pkg: CustomLabPackage;
    product: CustomLabPackageProduct;
    includedAssays: Array<{code: string; name: string; loinc: string | null}>;
    memberDiscountPct?: number | null;
}> = ({pkg, product, includedAssays, memberDiscountPct}) => {
    const oneTime = pkg.pricing.one_time_cents;
    const {addLine} = useCart();

    // v11 drops the 3-month payment plan from this card, so the only
    // purchase path left is the one-time kit. The cart line keeps its
    // existing name/description so checkout and order records are
    // unchanged by the visual rework.
    const addToCart = () => {
        addLine({
            kind: 'custom_kit',
            key: customKitLineKey('one_time'),
            mode: 'one_time',
            name: 'Custom Lab Package',
            description: 'One-time custom lab kit built from your latest results.',
            image_url: null,
            price_cents: oneTime,
            member_price_cents: applyMemberDiscount(oneTime, memberDiscountPct),
            assay_codes: pkg.assays.map((a) => a.code),
            product_ids: pkg.products.map((p) => p.id),
        });
    };

    const showMemberPrice = memberDiscountPct != null && memberDiscountPct > 0;

    return (
        <article
            className="relative overflow-hidden rounded-[34px] shadow-sm tracking-[-0.02em] text-white"
            style={{
                backgroundImage: `url(${biomatchKitBg})`,
                backgroundSize: 'cover',
                backgroundPosition: 'top center',
            }}>
            <div className="px-8 pt-9">
                {/* v11 moves the section header inside the card as a mint
                    pill. #ddfdfa is the designer's exact fill — part of the
                    same static Plus palette MyLabsPlusCard renders against. */}
                <div className="inline-flex items-center gap-3 rounded-full bg-[#ddfdfa] py-[13px] pl-7 pr-9">
                    <img src={biomatchIcon} alt="" aria-hidden className="h-[23px] w-[29px]" />
                    <span className="text-[29px] font-normal leading-none text-mld-plus-ink">BioMatch</span>
                </div>

                <p className="mt-4 text-[16.7px] leading-[26px] text-white">
                    <span className="font-semibold">Your Next Test is Ready.</span>
                    <br />
                    BioMatch analyzed your recent lab results to identify the best follow-up kit for you.
                </p>
            </div>

            {/* Fixed brand art: the same kit box renders for every matched
                product, so this is a bundled asset rather than the product's
                own image_url. Full-bleed — the PNG carries its own glow
                margin, which is what separates it from the copy above. */}
            <img src={biomatchKitBox} alt="" aria-hidden className="mt-1.5 w-full" />

            <div className="px-8 pb-11">
                <div className="inline-flex items-center gap-3 rounded-full border border-mld-primary/50 px-6 py-2">
                    <Icon name="dna" className="h-[17px] w-[13px] text-mld-primary" />
                    <span className="text-[16.7px] font-semibold uppercase text-mld-primary">
                        Matched based on your labs
                    </span>
                </div>

                {/* Name, description and price all come from the matched
                    storefront product — the card has nothing to say without
                    one, which is why the hero gates on it. */}
                <h2 className="mt-3.5 text-[23px] font-medium leading-tight tracking-normal text-mld-primary">
                    {product.name}
                </h2>

                {product.description && (
                    <p className="mt-3 text-[16.7px] leading-[26px] text-white">{product.description}</p>
                )}

                <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
                    <button
                        type="button"
                        onClick={addToCart}
                        className="h-[60px] min-w-[232px] shrink-0 cursor-pointer rounded-full border border-mld-primary/70 px-8 text-[20px] font-medium tracking-normal text-white transition hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-mld-primary/40">
                        Order Your Kit
                    </button>

                    <div className="text-right">
                        <p className="text-[34px] font-semibold leading-none text-white">{formatUSD(oneTime)}</p>
                        {showMemberPrice && (
                            <p className="mt-2 text-[15px] font-medium leading-none text-white">
                                Only {formatUSD(applyMemberDiscount(oneTime, memberDiscountPct))}{' '}
                                <Link
                                    to="/membership/benefits"
                                    className="text-[#5bcfde] underline-offset-2 hover:underline">
                                    with Plus
                                </Link>
                            </p>
                        )}
                    </div>
                </div>

                {includedAssays.length > 0 && (
                    <>
                        <div className="mt-9 flex items-center gap-2.5">
                            <Icon name="vial" className="h-5 w-5 text-mld-primary" />
                            <span className="text-[16.7px] font-semibold uppercase text-mld-primary">
                                Included In Your Panel
                            </span>
                        </div>
                        {/* One flowing bullet-separated run rather than chips.
                            This panel describes what the kit tests; the chips on
                            BiomarkersNeedAttentionCard describe the patient's
                            results. The two overlap by design, so this list is
                            deliberately NOT deduped against them. */}
                        <div className="mt-3.5 rounded-3xl border border-mld-primary/40 px-6 py-7">
                            <p className="text-[16.7px] leading-[29px] text-mld-primary">
                                {includedAssays.map((a) => a.name).join(' • ')}
                            </p>
                        </div>
                    </>
                )}
            </div>
        </article>
    );
};

// ── Curated card ──────────────────────────────────────────────────────
//
// CTA branches by `recommendation_type`:
//   - program     → enrollable. Adds a curated_rec line to the cart;
//                   checkout dispatches to /api/biomatch/enrollments/checkout
//                   so the kit ships and the employer cost-split is
//                   recorded against the biomatch_enrollment.
//   - supplement  → the linked storefront product is added to the cart
//                   as a regular product line. Employer-coverage on
//                   supplement recs is currently informational (the
//                   shop checkout charges the storefront price); a
//                   future change can apply the discount as a promo.
//   - care_pathway / referral / lifestyle → external link via the
//                   rec's `pathway_url` or `url`. No purchase flow.

interface CuratedCardProps {
    rec: CuratedCardData;
    onPrev?: () => void;
    onNext?: () => void;
    index?: number;
    total?: number;
    memberDiscountPct?: number | null;
}

const CuratedCard: React.FC<CuratedCardProps> = ({rec, onPrev, onNext, index = 0, total = 1, memberDiscountPct}) => {
    const priceCents = Math.round(Number(rec.price ?? 0) * 100);
    const memberPriceCents = rec.cart_products?.[0]?.member_price_cents ?? null;
    const {addLine, addItem} = useCart();
    const recType = rec.recommendation_type;

    // Patient amount after employer cost-split. Employer-full covers
    // everything (Fortis is skipped at checkout when total = 0);
    // employer-partial covers a percentage; otherwise the patient pays
    // the full kit price. Only applies to program-type curated recs.
    let patientAmountCents = priceCents;
    if (rec.billing_type === 'employer_full') {
        patientAmountCents = 0;
    } else if (rec.billing_type === 'employer_partial' && typeof rec.employer_coverage_percent === 'number') {
        const coverage = Math.max(0, Math.min(100, rec.employer_coverage_percent));
        patientAmountCents = Math.round(priceCents * (1 - coverage / 100));
    }

    const linkedProduct = rec.cart_products?.[0] ?? null;
    const externalUrl = rec.pathway_url || rec.url || null;

    // Whether the CTA renders as a button (cart) vs. an anchor (link)
    // vs. is suppressed entirely.
    const ctaMode: 'enroll' | 'buy_product' | 'external' | 'none' =
        recType === 'program'
            ? 'enroll'
            : recType === 'supplement' && linkedProduct
              ? 'buy_product'
              : externalUrl
                ? 'external'
                : 'none';

    // Base regular-price amount each footer button displays — the member
    // price line applies the discount to this same base AND renders under the
    // same guard the displayed regular price uses (see footer). One-Time is
    // gated by `priceCents > 0` (shows "—" otherwise, even in buy_product
    // mode), so the member line uses that guard while discounting the value
    // actually shown (`linkedProduct.price_cents` in buy_product mode).
    const oneTimeBaseCents = ctaMode === 'buy_product' && linkedProduct ? linkedProduct.price_cents : priceCents;
    // Mirror the membership display's own fallback: a member price of exactly
    // 0 falls back to priceCents (which `??` would NOT do).
    const memberBaseCents = memberPriceCents != null && memberPriceCents > 0 ? memberPriceCents : priceCents;

    const addToCart = () => {
        if (ctaMode === 'enroll') {
            addLine({
                kind: 'curated_rec',
                key: curatedRecLineKey(rec.id),
                recommendation_id: rec.id,
                name: rec.name,
                description: rec.cta_body_text ?? undefined,
                // Resolve before stashing — cart UI just renders the URL
                // and shouldn't need to know about API-relative paths.
                image_url: rec.image ? resolveMediaUrl(rec.image) : null,
                price_cents: priceCents,
                patient_amount_cents: patientAmountCents,
                kit_price_cents: priceCents,
                // No member_price_cents here — the curated/program
                // enrollment checkout (finalizeBioMatchCheckout) is not
                // member-discount-aware server-side; it always charges
                // patient_amount_cents. Setting a discounted price on
                // this line would show the patient a cart total the
                // server never actually charges.
            });
            return;
        }
        if (ctaMode === 'buy_product' && linkedProduct) {
            // Storefront product images come back as relative API
            // paths — resolve so the cart drawer renders them.
            addItem({
                ...linkedProduct,
                image_url: linkedProduct.image_url ? resolveMediaUrl(linkedProduct.image_url) : null,
            });
        }
    };
    const productName = linkedProduct?.name ?? rec.name;
    const kicker = linkedProduct?.category ?? rec.recommendation_type ?? 'BioMatched';
    const description = rec.cta_body_text || '';

    // "Based On Your <lab value> Value" — the lab value(s) whose rule
    // matched to recommend this product. Prefer true lab biomarkers over
    // biometric/age rules; fall back to a generic label when none are present.
    const basedOnLabel = (() => {
        const rules = rec.biomarkers ?? [];
        const biomarkerRules = rules.filter((b) => (b.rule_source ?? 'biomarker') === 'biomarker');
        const source = biomarkerRules.length > 0 ? biomarkerRules : rules;
        const names = Array.from(
            new Set(source.map((b) => cleanBiomarkerName(b.test_type || '').trim()).filter(Boolean))
        );
        if (names.length === 0) return 'Based On Your Labs';
        if (names.length === 1) return `Based On Your ${names[0]} Value`;
        return `Based On Your ${names.slice(0, 2).join(' & ')} Values`;
    })();

    return (
        <article
            className="relative overflow-hidden rounded-2xl shadow-sm text-white"
            style={{
                backgroundImage: `url(${biomatchProductBg})`,
                backgroundSize: 'cover',
                backgroundPosition: 'top center',
            }}>
            {/* Carousel arrows */}
            {total > 1 && (
                <>
                    <button
                        type="button"
                        onClick={onPrev}
                        aria-label="Previous recommendation"
                        className="absolute left-2 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/15 p-1 text-white hover:bg-white/25 transition-colors">
                        <Icon name="chevron-left" className="h-4 w-4" />
                    </button>
                    <button
                        type="button"
                        onClick={onNext}
                        aria-label="Next recommendation"
                        className="absolute right-2 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/15 p-1 text-white hover:bg-white/25 transition-colors">
                        <Icon name="chevron-right" className="h-4 w-4" />
                    </button>
                </>
            )}

            {/* Top pill */}
            <div className="px-5 pt-5">
                <div
                    className="inline-flex items-center gap-1.5 rounded-full border
                bg-mld-card-text/30 px-3 py-1
                text-[10px] font-semibold uppercase tracking-wider text-mld-primary/95">
                    <Icon name="dna" className="h-3 w-3" />
                    Biomatched based on your results
                </div>
            </div>

            {/* Kicker + title */}
            <div className="mt-3 px-8 text-center">
                <p className="text-xs text-white/70">{kicker}</p>
                <h2 className="mt-1 text-xl font-semibold leading-tight text-white">{productName}</h2>
            </div>

            {/* Product image — framed in a white rounded card per the design. */}
            <div className="mx-5 mt-3 flex h-44 items-center justify-center rounded-2xl bg-white px-4">
                {linkedProduct?.image_url || rec.image ? (
                    <img
                        src={resolveMediaUrl(linkedProduct?.image_url ?? rec.image ?? '')}
                        alt={productName}
                        className="max-h-[150px] max-w-[60%] object-contain"
                    />
                ) : (
                    <Icon name="droplet" className="h-16 w-16 text-mld-primary/40" />
                )}
            </div>

            {/* "Based On Your <lab value> Value" pill — soft amber. */}
            <div className="mt-3 flex justify-center px-5">
                <div
                    className="inline-flex items-center leading-none gap-1.5 rounded-md
                bg-amber-100 border border-amber-900 px-4 py-1.5 text-[11px] font-medium text-amber-900">
                    <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                    <span>{basedOnLabel}</span>
                </div>
            </div>

            {/* Description */}
            {description && (
                <p className="mt-3 px-5 text-center text-xs leading-relaxed text-white/90 line-clamp-3">
                    {description}
                </p>
            )}

            {/* Footer pricing CTAs */}
            {ctaMode !== 'none' && (
                <div className="mt-4 grid grid-cols-2 gap-2 px-5">
                    <button
                        type="button"
                        onClick={ctaMode === 'external' ? undefined : addToCart}
                        className="flex flex-col items-start gap-0.5 rounded-xl bg-white px-3 py-2.5 text-left transition hover:bg-white/90">
                        <span className="text-[10px] font-medium text-mld-muted-text">One-Time</span>
                        <span className="text-base font-semibold leading-none text-mld-text-primary">
                            {priceCents > 0
                                ? ctaMode === 'buy_product' && linkedProduct
                                    ? formatUSD(linkedProduct.price_cents)
                                    : formatUSD(priceCents)
                                : '—'}
                        </span>
                        {priceCents > 0 && memberDiscountPct != null && memberDiscountPct > 0 && (
                            <span className="mt-1 inline-flex w-fit items-center gap-1 rounded-full bg-mld-plus-ink px-2 py-0.5 text-[10px] font-semibold text-mld-plus-mint">
                                <span aria-hidden="true">✦</span>
                                {formatUSD(applyMemberDiscount(oneTimeBaseCents, memberDiscountPct))} with My Labs+
                            </span>
                        )}
                        <span className="text-[9px] text-mld-muted-text">Order now - pay once</span>
                    </button>
                    <div className="rounded-xl bg-white">
                        <button
                            type="button"
                            onClick={ctaMode === 'external' ? undefined : addToCart}
                            className="w-full flex flex-col items-start gap-0.5 rounded-xl bg-mld-primary/8 ring-2 ring-mld-primary px-3 py-2.5 text-left transition hover:bg-white/80">
                            <span className="text-[10px] font-medium text-mld-primary">My Labs Membership</span>
                            <span className="text-base font-semibold leading-none text-mld-text-primary">
                                {memberPriceCents != null && memberPriceCents > 0
                                    ? formatUSD(memberPriceCents)
                                    : priceCents > 0
                                      ? formatUSD(priceCents)
                                      : '—'}
                                <span className="text-xs font-normal text-mld-muted-text">/mo</span>
                            </span>
                            {memberBaseCents > 0 && memberDiscountPct != null && memberDiscountPct > 0 && (
                                <span className="mt-1 inline-flex w-fit items-center gap-1 rounded-full bg-mld-plus-ink px-2 py-0.5 text-[10px] font-semibold text-mld-plus-mint">
                                    <span aria-hidden="true">✦</span>
                                    {formatUSD(applyMemberDiscount(memberBaseCents, memberDiscountPct))}/mo with My
                                    Labs+
                                </span>
                            )}
                            <span className="text-[9px] text-mld-muted-text">Delivered Monthly</span>
                        </button>
                    </div>
                </div>
            )}

            {/* Carousel dots + count */}
            <div className="mt-4 flex flex-col items-center gap-1 pb-4">
                {total > 1 && (
                    <div className="flex items-center gap-1">
                        {Array.from({length: total}).map((_, i) => (
                            <span
                                key={i}
                                className={clsx(
                                    'h-1.5 w-1.5 rounded-full transition-colors',
                                    i === index ? 'bg-white' : 'bg-white/30'
                                )}
                            />
                        ))}
                    </div>
                )}
                <p className="text-[11px] text-mld-primary">
                    {total} Product{total === 1 ? '' : 's'} Recommended
                </p>
            </div>

            {/* External-link CTA, rendered as anchor above the pricing
                CTAs when this is a referral / pathway. */}
            {ctaMode === 'external' && externalUrl && (
                <a
                    href={externalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="absolute inset-0 z-0"
                    aria-label={rec.cta_button_label || 'Learn more'}
                />
            )}
        </article>
    );
};

export default CustomBioMatchHero;
